import { useCallback } from "react"
import {
    getErrorCode,
    getResponseErrorMessage,
    isErrorWithMessage,
    isKrakenErrorResponse,
} from "@krakentech/blueprint-api"
import { type ErrorIntlConfig } from "../types/errorIntlConfig"
import { type Namespace } from "../types/namespace"
import { createUseFormatErrorCode } from "./createUseFormatErrorCode"

type CreateUseFormatRequestErrorConfig<TMessages, TNamespace extends Namespace<TMessages>> = Pick<
    ErrorIntlConfig<TMessages, TNamespace>,
    "namespace" | "formatErrorCode"
>

export function createUseFormatRequestError<TMessages, TNamespace extends Namespace<TMessages>>({
    namespace,
    formatErrorCode,
}: CreateUseFormatRequestErrorConfig<TMessages, TNamespace>) {
    const useFormatErrorCode = createUseFormatErrorCode<TMessages, TNamespace>({
        namespace,
        formatErrorCode,
    })

    function useFormatRequestError() {
        const formatErrorCode = useFormatErrorCode()

        return useCallback(
            (error: unknown) => {
                if (!error) {
                    return null
                }

                if (typeof error === "string") {
                    return error
                }

                if (isKrakenErrorResponse(error)) {
                    const errorCode = getErrorCode(error)

                    if (errorCode) {
                        return formatErrorCode(errorCode)
                    }

                    const errorMessage = getResponseErrorMessage(error)

                    if (errorMessage) {
                        return errorMessage
                    }
                } else if (isErrorWithMessage(error)) {
                    return error.message
                }

                return formatErrorCode("UNKNOWN_ERROR")
            },
            [formatErrorCode]
        )
    }

    useFormatRequestError.messages = [...useFormatErrorCode.messages] as const

    return useFormatRequestError
}
